import moment from 'moment-timezone';
import { DateRangePresetKey } from '../types';

export type DateRangeValue = {
  start: Date | null;
  end: Date | null;
};

export type DateRangePreset = {
  key: DateRangePresetKey;
  label: string;
  getRange: () => DateRangeValue;
};

const startOfDay = (m: moment.Moment) => m.clone().startOf('day').toDate();
const endOfDay = (m: moment.Moment) => m.clone().endOf('day').toDate();

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    key: 'today',
    label: 'Hoje',
    getRange: () => {
      const today = moment();
      return { start: startOfDay(today), end: endOfDay(today) };
    }
  },
  {
    key: 'yesterday',
    label: 'Ontem',
    getRange: () => {
      const yesterday = moment().subtract(1, 'day');
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    }
  },
  {
    key: 'thisWeek',
    label: 'Esta semana (Dom - Hoje)',
    getRange: () => {
      const today = moment();
      const sunday = today.clone().startOf('week');
      return { start: startOfDay(sunday), end: endOfDay(today) };
    }
  },
  {
    key: 'last7Days',
    label: 'Últimos 7 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(6, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'lastWeek',
    label: 'Semana passada (Dom - Sáb)',
    getRange: () => {
      const lastSunday = moment().subtract(1, 'week').startOf('week');
      const lastSaturday = lastSunday.clone().endOf('week');
      return { start: startOfDay(lastSunday), end: endOfDay(lastSaturday) };
    }
  },
  {
    key: 'last28Days',
    label: 'Últimos 28 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(27, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'last30Days',
    label: 'Últimos 30 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(29, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'thisMonth',
    label: 'Este mês',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().startOf('month')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'lastMonth',
    label: 'Mês passado',
    getRange: () => {
      const lastMonth = moment().subtract(1, 'month');
      return {
        start: startOfDay(lastMonth.clone().startOf('month')),
        end: endOfDay(lastMonth.clone().endOf('month'))
      };
    }
  },
  {
    key: 'last90Days',
    label: 'Últimos 90 dias',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().subtract(89, 'days')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'thisYear',
    label: 'Este ano',
    getRange: () => {
      const today = moment();
      return {
        start: startOfDay(today.clone().startOf('year')),
        end: endOfDay(today)
      };
    }
  },
  {
    key: 'custom',
    label: 'Personalizado',
    getRange: () => ({ start: null, end: null })
  }
];

export const getPresetByKey = (key: DateRangePresetKey): DateRangePreset | undefined =>
  DATE_RANGE_PRESETS.find(preset => preset.key === key);

export const detectPreset = (
  start: Date | null | undefined,
  end: Date | null | undefined
): DateRangePresetKey => {
  if (!start || !end) return 'custom';

  const startStr = moment(start).format('YYYY-MM-DD');
  const endStr = moment(end).format('YYYY-MM-DD');

  for (const preset of DATE_RANGE_PRESETS) {
    if (preset.key === 'custom') continue;
    const range = preset.getRange();
    if (
      range.start &&
      range.end &&
      moment(range.start).format('YYYY-MM-DD') === startStr &&
      moment(range.end).format('YYYY-MM-DD') === endStr
    ) {
      return preset.key;
    }
  }

  return 'custom';
};

export const formatDateBR = (date: Date | null | undefined): string => {
  if (!date) return '';
  return moment(date).format('DD/MM/YYYY');
};

export const parseDateBR = (value: string): Date | null => {
  if (!value) return null;
  const parsed = moment(value, 'DD/MM/YYYY', true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const formatDateRangeLabel = (
  start: Date | null | undefined,
  end: Date | null | undefined
): string => {
  if (!start && !end) return '';
  if (start && end) return `${formatDateBR(start)} - ${formatDateBR(end)}`;
  if (start) return formatDateBR(start);
  return formatDateBR(end);
};
